import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import TabbedContent from '../components/TabbedContent';
import '../styles/InfoPages.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function DynamicPage({ pageId: staticPageId = null }) {
  const { pageId: routePageId } = useParams();
  const pageId = staticPageId || routePageId;
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const containerRef = useRef(null);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching page content:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [pageId]);

  // Handle form submission for contact form
  useEffect(() => {
    if (!pageId || pageId !== 'contact') return;

    const handleFormSubmit = async (e) => {
      // Only intercept form submissions
      if (e.target.tagName !== 'FORM') return;

      console.log('Contact form submitted', e.target);

      // Get form data
      const formData = new FormData(e.target);
      const name = formData.get('name')?.trim();
      const email = formData.get('email')?.trim();
      const phone = formData.get('phone')?.trim();
      const message = formData.get('message')?.trim();

      console.log('Form data:', { name, email, phone, message });

      // Only intercept if this looks like a contact form (has at least name, email, message)
      if (!name || !email || !message) {
        console.log('Missing required fields');
        e.preventDefault();
        setFormMessage('✗ Please fill in all required fields (Name, Email, Message)');
        return;
      }

      e.preventDefault();
      setFormMessage('');

      try {
        console.log('Sending to API:', `${API_BASE_URL}/api/contact-form`);
        const response = await fetch(`${API_BASE_URL}/api/contact-form`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            email,
            phone: phone || null,
            message,
            userId: user?.id || null
          })
        });

        const data = await response.json();
        console.log('API response:', response.status, data);

        if (response.ok) {
          setFormMessage('✓ Thank you for your message! We\'ll get back to you soon.');
          e.target.reset();
          setTimeout(() => setFormMessage(''), 5000);
        } else {
          setFormMessage(`✗ Error: ${data.error || 'Failed to submit form'}`);
        }
      } catch (err) {
        console.error('Error submitting contact form:', err);
        setFormMessage('✗ Error submitting form. Please try again.');
      }
    };

    // Use document as listener to catch all form submissions
    document.addEventListener('submit', handleFormSubmit, true);
    return () => document.removeEventListener('submit', handleFormSubmit, true);
  }, [pageId, user, token]);

  if (loading) {
    return <div className="info-page"><p>Loading...</p></div>;
  }

  if (error) {
    return (
      <div className="info-page">
        <div className="info-container">
          <h1>Page Not Found</h1>
          <p>The page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Check if content is a full-screen page (contains 100vh)
  const isFullScreen = pageContent?.content?.includes('100vh');
  // Check if content has tabs
  const hasTabs = pageContent?.content?.includes('class="tab"');

  return (
    <div className={`info-page ${isFullScreen ? 'full-screen' : ''}`} ref={containerRef}>
      {formMessage && (
        <div style={{
          padding: '1rem',
          margin: '1rem',
          borderRadius: '4px',
          textAlign: 'center',
          backgroundColor: formMessage.includes('✓') ? '#d4edda' : '#f8d7da',
          color: formMessage.includes('✓') ? '#155724' : '#721c24',
          border: `1px solid ${formMessage.includes('✓') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {formMessage}
        </div>
      )}
      {!isFullScreen && <div className="info-container">
        {hasTabs ? (
          <TabbedContent htmlContent={pageContent?.content || ''} />
        ) : (
          <section
            className="info-section"
            dangerouslySetInnerHTML={{ __html: pageContent?.content || '' }}
          />
        )}
      </div>}
      {isFullScreen && (
        <section
          className="info-section full-screen-content"
          dangerouslySetInnerHTML={{ __html: pageContent?.content || '' }}
        />
      )}
    </div>
  );
}
